import { WallsGame } from '../../games/walls/WallsGame.js';
import { WallsVortex } from '../../games/walls/WallsVortex.js';
import { WallsRenderer } from '../../games/walls/WallsRenderer.js';
import { Grid } from '../../core/Grid.js';
import { Position, positionKey } from '../../core/GridGeometry.js';
import { RenderConfig } from '../../rendering/Renderer.js';
import { TutorialGameAdapter } from '../TutorialEngine.js';

// ── Walls-specific types ──────────────────────────────────────────

export interface WallsRenderOptions {
    // Placeholder — WallsRenderer is fully game-state driven
}

// ── Adapter ─────────────────────────────────────────────────────

export class WallsTutorialAdapter implements TutorialGameAdapter {
    private game: WallsGame;
    private renderer: WallsRenderer;

    constructor(canvas: HTMLCanvasElement, grid: Grid) {
        this.game = new WallsGame(grid);
        this.renderer = new WallsRenderer(canvas, grid);
        // Static scenes: no tweening, so the renderer never self-re-renders and
        // wipes the engine-drawn annotations.
        this.renderer.setSelfAnimation(false);
    }

    applyState(state: unknown): void {
        const serialized = state as string;
        this.game.reset();
        if (serialized) {
            this.game.deserialize(serialized);
        }
    }

    render(_options: unknown): void {
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createWallsTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    return new WallsTutorialAdapter(canvas, grid);
}

// ── Vortex state ─────────────────────────────────────────────────

export interface WallsVortexTutorialState {
    walls: string;
    gateMode?: boolean;
    /**
     * Extra gate pairs (cell strings, e.g. ['B5', 'D5']) merged on top of the
     * adapter's default teleport pair for this scene only — used by the
     * activation scene to show a second, blocked (amber) pair. Omit otherwise.
     */
    extraGates?: Array<[string, string]>;
}

/** Parse a cell string like "B5" into a Position ({ row: 5, col: 1 }). */
function parseCellString(s: string): Position {
    return { col: s.charCodeAt(0) - 65, row: parseInt(s.slice(1), 10) };
}


// ── Vortex Adapter ───────────────────────────────────────────────

export class WallsVortexTutorialAdapter implements TutorialGameAdapter {
    private game: WallsVortex;
    private renderer: WallsRenderer;
    private gatePairs: Array<[Position, Position]>;
    private allGates: Set<string>;
    private currentGateMode: boolean = false;

    constructor(
        canvas: HTMLCanvasElement,
        grid: Grid,
        gatePairs: Array<[Position, Position]>,
    ) {
        this.game = new WallsVortex(grid);
        this.renderer = new WallsRenderer(canvas, grid);
        this.renderer.setSelfAnimation(false);   // static scenes — see the Base adapter
        this.gatePairs = gatePairs;
        this.allGates = new Set<string>();
        for (const [a, b] of gatePairs) {
            this.allGates.add(positionKey(a));
            this.allGates.add(positionKey(b));
        }
    }

    applyState(state: unknown): void {
        const s = state as WallsVortexTutorialState | null;
        this.game.reset();
        const pairs = [...this.gatePairs];
        for (const [a, b] of s?.extraGates ?? []) {
            pairs.push([parseCellString(a), parseCellString(b)]);
        }
        const allGates = new Set<string>();
        for (const [a, b] of pairs) {
            allGates.add(positionKey(a));
            allGates.add(positionKey(b));
        }
        this.game.setVortexData(pairs, allGates);

        if (!s) return;

        if (s.walls) {
            this.game.deserialize(s.walls);
        }

        this.currentGateMode = s.gateMode ?? false;
    }

    render(_options: unknown): void {
        this.renderer.setGateMode(this.currentGateMode);
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createWallsVortexTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    const gatePairs: Array<[Position, Position]> = [
        [{ row: 4, col: 0 }, { row: 3, col: 3 }],  // A4 ↔ D3
    ];
    return new WallsVortexTutorialAdapter(canvas, grid, gatePairs);
}
